
import { useState } from 'react';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

interface DateInputFieldProps {
  selectedDate: Date;
  onDateChange: (date: Date | string) => void;
}

export default function DateInputField({ selectedDate, onDateChange }: DateInputFieldProps) {
  const [dateInputType, setDateInputType] = useState<'calendar' | 'manual'>('calendar');

  const toggleDateInputType = () => {
    setDateInputType(prev => prev === 'calendar' ? 'manual' : 'calendar');
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <Label htmlFor="record_date">Date*</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleDateInputType}
          className="h-6 text-xs text-muted-foreground"
        >
          {dateInputType === 'calendar' ? 'Enter Manually' : 'Use Calendar'}
        </Button>
      </div>

      {dateInputType === 'calendar' ? (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              id="record_date"
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !selectedDate && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {selectedDate ? format(selectedDate, "PPP") : "Select a date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && onDateChange(date)}
              initialFocus
              className={cn("p-3 pointer-events-auto")}
            />
          </PopoverContent>
        </Popover>
      ) : (
        <Input
          id="record_date"
          name="record_date"
          type="date"
          value={selectedDate ? format(selectedDate, "yyyy-MM-dd") : ""}
          onChange={(e) => onDateChange(e.target.value ? new Date(e.target.value + 'T00:00:00') : "")}
          max={format(new Date(), "yyyy-MM-dd")}
        />
      )}
    </div>
  );
}
